import { Icon } from '@iconify/react/dist/iconify.js'
import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import axios from 'axios'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { API_URL } from '..'

const SignInLayer = () => {
    const [searchParams] = useSearchParams();
    const id = searchParams.get('id'); // locationId from URL
    const tk_id = searchParams.get('tk_id'); // API token from URL

    const [formData, setFormData] = useState({
        email: '',
        locationId: localStorage.getItem('locationID') || id || ''
    })
    const [code, setCode] = useState('')
    const [isCodeView, setIsCodeView] = useState(false)
    const [loading, setLoading] = useState(false)
    const [matchedUser, setMatchedUser] = useState(null)
    const [showModal, setShowModal] = useState(false)
    const [tempToken, setTempToken] = useState(localStorage.getItem('tokenID') || tk_id || '')
    const [tempLocationId, setTempLocationId] = useState(formData.locationId)
    const [modalError, setModalError] = useState('')
    const [modalSuccess, setModalSuccess] = useState('')

    const navigate = useNavigate();

    useEffect(() => {
        const isUser = JSON.parse(localStorage.getItem('user'));
        if (isUser) {
            navigate('/')
        }

        // Show modal and validate credentials if tokenID or locationID is missing
        if (!localStorage.getItem('tokenID') || !localStorage.getItem('locationID')) {
            // setShowModal(true)
            if (tempToken && tempLocationId) {
                validateCredentials();
            }
        }
    }, [navigate, tempToken, tempLocationId])

    const validateCredentials = async () => {
        if (!tempToken.trim()) {
            setModalError('API Token is required')
            return
        }
        if (!tempLocationId.trim()) {
            setModalError('Location ID is required')
            return
        }

        setModalError('')
        setModalSuccess('')
        setLoading(true)
        try {
            const options = {
                method: 'GET',
                url: 'https://services.leadconnectorhq.com/users/',
                params: { locationId: tempLocationId },
                headers: {
                    Authorization: `Bearer ${tempToken}`,
                    Version: '2021-07-28',
                    Accept: 'application/json'
                }
            }
            
            const response = await axios.request(options)
            if (response.status === 200) {
                // Save valid credentials to localStorage
                localStorage.setItem('tokenID', tempToken)
                localStorage.setItem('locationID', tempLocationId)
                setFormData(prev => ({ ...prev, locationId: tempLocationId }))
                setModalSuccess('Setup with this CRM is confirmed')
                setTimeout(() => setShowModal(false), 1500) // Close modal after showing success
            } else {
                throw new Error('Invalid credentials')
            }
        } catch (error) {
            setModalError(error.response?.data?.message || 'Invalid API Token or Location ID. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    const onFormSubmission = async (event) => {
        event.preventDefault()
        if (!formData.locationId) {
            setModalError('Location ID is required. Please set it first.')
            setShowModal(true)
            return
        }
        setLoading(true)
        try {
            const storedToken = localStorage.getItem('tokenID')
            if (!storedToken) {
                setModalError('API Token is required. Please set it first.')
                setShowModal(true)
                throw new Error('No authentication token found')
            }

            // Step 1: Search for user
            const options = {
                method: 'GET',
                url: 'https://services.leadconnectorhq.com/users/',
                params: { locationId: formData.locationId },
                headers: {
                    Authorization: `Bearer ${storedToken}`,
                    Version: '2021-07-28',
                    Accept: 'application/json'
                }
            }

            const { data } = await axios.request(options)
            const matchedUser = data.users?.find(user => user.email.toLowerCase() === formData.email.toLowerCase())

            if (matchedUser) {
                // Step 2: Send verification code
                const sendCodeOptions = {
                    method: 'POST',
                    url: API_URL + '/api/user/send-verification-code',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    data: {
                        email: formData.email,
                        locationId: formData.locationId
                    }
                }

                await axios.request(sendCodeOptions)
                setMatchedUser(matchedUser)
                setIsCodeView(true)
                toast.success('Verification code sent to your email', {
                    position: 'top-right',
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true
                })
            } else {
                toast.error('No user found with the provided email', {
                    position: 'top-right',
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true
                })
            }
        } catch (error) {
            toast.error(`Error: ${error.message || 'Failed to search user or send code'}`, {
                position: 'top-right',
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true
            })
        } finally {
            setLoading(false)
        }
    }
    const fetchPrivileges = async (id) => {
        try {
            const response = await fetch(`${API_URL}/api/privileges/${id}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            const data = await response.json();
            if (response.status === 200) {
                return (data.privileges || {
                    invoice: { edit: false, delete: false, read: false, changeStatus: false },
                    quotes: { edit: false, delete: false, read: false, changeStatus: false }
                });
                
            } else {
                throw new Error('Failed to fetch privileges');
            }
        } catch (err) {
            console.error('Fetch privileges error:', err);
            alert('Error fetching privileges: ' + err.message);
        }
    };
    const onCodeSubmission = async (event) => {
        event.preventDefault()
        setLoading(true)
        try {
            const verifyCodeOptions = {
                method: 'POST',
                url: API_URL + '/api/user/verify-code',
                headers: {
                    'Content-Type': 'application/json',
                },
                data: {
                    email: formData.email,
                    code: code,
                    locationId: formData.locationId
                }
            }

            const { data } = await axios.request(verifyCodeOptions)
            if (data.success) {
                const id = await fetchPrivileges(matchedUser?.id)
                if(id){
                    localStorage.setItem('user', JSON.stringify(matchedUser))
                    localStorage.setItem('locationID', formData.locationId)
                    localStorage.setItem('privilages', JSON.stringify(id))
                }
                toast.success('User verified successfully', {
                    position: 'top-right',
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true
                })
                navigate('/')

            } else {
                toast.error('Invalid verification code', {
                    position: 'top-right',
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true
                })
            }
        } catch (error) {
            toast.error(`Error verifying code: ${error.message || 'Unknown error'}`, {
                position: 'top-right',
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true
            })
        } finally {
            setLoading(false)
        }
    }
    

    return (
        <section className="auth bg-base d-flex flex-wrap">
            <style>
                {`
                    .modal-animated .modal-dialog {
                        animation: scaleIn 0.3s ease-out;
                    }
                    @keyframes scaleIn {
                        0% {
                            opacity: 0;
                            transform: scale(0.8);
                        }
                        100% {
                            opacity: 1;
                            transform: scale(1);
                        }
                    }
                    .modal-content {
                        border-radius: 12px;
                        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
                    }
                    .modal-header {
                        background: linear-gradient(90deg, #007bff, #0056b3);
                        color: white;
                        border-top-left-radius: 12px;
                        border-top-right-radius: 12px;
                    }
                    .modal-body input {
                        transition: border-color 0.3s ease, box-shadow 0.3s ease;
                    }
                    .modal-body input:focus {
                        border-color: #007bff;
                        box-shadow: 0 0 8px rgba(0, 123, 255, 0.3);
                    }
                    .modal-body .alert-success {
                        animation: fadeIn 0.5s ease-in;
                    }
                    @keyframes fadeIn {
                        0% { opacity: 0; }
                        100% { opacity: 1; }
                    }
                `}
            </style>
            <div className="auth-left d-lg-block d-none">
                <div className="d-flex align-items-center flex-column h-100 justify-content-center">
                    <img src="assets/images/auth/auth-img.png" alt="" />
                </div>
            </div>
            <div className="auth-right py-32 px-24 d-flex flex-column justify-content-center">
                <div className="max-w-464-px mx-auto w-100">
                    <div>
                        <Link to="/" className="mb-40 max-w-290-px">
                            <img src="assets/images/logo.png" alt="" />
                        </Link>
                        <h4 className="mb-12">{isCodeView ? 'Enter Verification Code' : 'Sign in'}</h4>
                        <p className="mb-32 text-secondary-light text-lg">
                            {isCodeView 
                                ? 'Enter the 4-digit code sent to your email'
                                : 'Enter your email to login into your account'}
                        </p>
                    </div>
                    {!isCodeView ? (
                        <form onSubmit={onFormSubmission}>
                            <div className="icon-field mb-16">
                                <span className="icon top-50 translate-middle-y">
                                    <Icon icon="mage:email" />
                                </span>
                                <input
                                    type="email"
                                    className="form-control h-56-px bg-neutral-50 radius-12"
                                    placeholder="Enter your email"
                                    required
                                    value={formData.email}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                                    disabled={showModal}
                                />
                            </div>
                            <button
                                type="submit"
                                className="btn btn-primary text-sm btn-sm px-12 py-16 w-100 radius-12 mt-32"
                                disabled={loading || showModal}
                            >
                                {loading ? (
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                ) : (
                                    'Sign in'
                                )}
                            </button>
                        </form>
                    ) : (
                        <form onSubmit={onCodeSubmission}>
                            <div className="icon-field mb-20">
                                <span className="icon top-50 translate-middle-y">
                                    <Icon icon="solar:code-circle" />
                                </span>
                                <input
                                    type="text"
                                    className="form-control h-56-px bg-neutral-50 radius-12"
                                    placeholder="Enter 4-digit code"
                                    required
                                    maxLength="4"
                                    pattern="\d{4}"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                />
                            </div>
                            <button
                                type="submit"
                                className="btn btn-primary text-sm btn-sm px-12 py-16 w-100 radius-12 mt-32"
                                disabled={loading}
                            >
                                {loading ? (
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                ) : (
                                    'Verify Code'
                                )}
                            </button>
                        </form>
                    )}
                </div>
            </div>

            {/* GHL Credentials Modal */}
            {showModal && (
                <div className="modal fade show d-block modal-animated" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} data-bs-backdrop="static" data-bs-keyboard="false">
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Configure FrontForce CRM</h5>
                            </div>
                            <div className="modal-body">
                                <p className="text-secondary-light mb-4">Please provide your FrontForce API Token and Location ID to connect to the CRM.</p>
                                {modalError && (
                                    <div className="alert alert-danger alert-dismissible fade show mb-4" role="alert">
                                        {modalError}
                                        <button type="button" className="btn-close" onClick={() => setModalError('')} aria-label="Close"></button>
                                    </div>
                                )}
                                {modalSuccess && (
                                    <div className="alert alert-success alert-dismissible fade show mb-4" role="alert">
                                        {modalSuccess}
                                        <button type="button" className="btn-close" onClick={() => setModalSuccess('')} aria-label="Close"></button>
                                    </div>
                                )}
                                <div className="mb-3">
                                    <label htmlFor="tokenInput" className="form-label fw-bold">API Token</label>
                                    <input
                                        type="text"
                                        className="form-control form-control-lg"
                                        id="tokenInput"
                                        value={tempToken}
                                        onChange={(e) => setTempToken(e.target.value)}
                                        placeholder="Enter FrontForce API Token"
                                        required
                                    />
                                    <div className="form-text">Your FrontForce API token from the agency settings.</div>
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="locationIdInput" className="form-label fw-bold">Location ID</label>
                                    <input
                                        type="text"
                                        className="form-control form-control-lg"
                                        id="locationIdInput"
                                        value={tempLocationId}
                                        onChange={(e) => setTempLocationId(e.target.value)}
                                        placeholder="Enter FrontForce Location ID"
                                        required
                                    />
                                    <div className="form-text">The Location ID associated with your FrontForce account.</div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-primary btn-lg" 
                                    onClick={validateCredentials}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Validating...
                                        </>
                                    ) : (
                                        'Save and Connect'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <ToastContainer />
        </section>
    )
}

export default SignInLayer